/**
 * §12.4 — personal results, laid out as docs/ux-mockup.html draws them.
 *
 * Outcome badge, the correct answer, the points counting up in board gold, then
 * where that left the player in the room. The explanation follows, because that
 * is where the learning happens (§7.2).
 */

import { useCountUp } from "@tq/ui";
import { describeRankDelta, type GradingOutcome } from "@tq/domain";
import type { EventPayload } from "@tq/protocol";
import { ordinal } from "./StandingBar.js";

export interface PersonalResultProps {
  payload: EventPayload<"player.result.published">;
}

/**
 * Keyed by the grading outcome itself, so a new or renamed outcome is a type
 * error here rather than a raw enum value on a player's screen. The loose map
 * this replaces printed `no_answer` to the room, unstyled, because the mockup
 * called that case `none`.
 */
const OUTCOME_TEXT: Record<GradingOutcome, { label: string; variant: string }> = {
  correct: { label: "✔ Correct!", variant: "correct" },
  partial: { label: "± Partial Credit", variant: "partial" },
  incorrect: { label: "✘ Incorrect", variant: "incorrect" },
  no_answer: { label: "⏱ No Answer", variant: "none" },
};

export function PersonalResult({ payload }: PersonalResultProps): React.ReactElement {
  const points = useCountUp(payload.points);
  const outcome = OUTCOME_TEXT[payload.outcome];

  return (
    <>
      {/* §29.2 — the outcome is a word and a symbol, never color alone. */}
      <div className={`tq-phone-result-badge tq-phone-result-badge--${outcome.variant}`}>
        {outcome.label}
      </div>

      {payload.ownAnswer && (
        <div className="tq-phone-result-answer" style={{ marginBottom: 8 }}>
          Your answer: <b style={{ color: "var(--ink-on-light)" }}>{payload.ownAnswer}</b>
        </div>
      )}

      <div className="tq-phone-result-answer">
        Correct answer: <b>{payload.correctAnswer}</b>
      </div>

      <div className="tq-phone-result-points">
        {payload.points > 0 ? "+" : ""}
        {points}
      </div>

      {/* §12.4 — where that answer left them, and which way they moved. */}
      <div className="tq-phone-rank-move">{rankMove(payload)}</div>

      {payload.explanation && <p className="tq-phone-idle-text">{payload.explanation}</p>}
    </>
  );
}

function rankMove(payload: EventPayload<"player.result.published">): React.ReactNode {
  if (payload.overallRank == null) return "Answer a question to get on the board";

  const here = (
    <>
      now <b>{ordinal(payload.overallRank)}</b>
    </>
  );

  if (payload.rankDelta != null && payload.rankDelta > 0) {
    return (
      <>
        <span className="tq-up">
          ⬆ Up {payload.rankDelta} spot{payload.rankDelta > 1 ? "s" : ""}
        </span>{" "}
        — {here}
      </>
    );
  }
  if (payload.rankDelta != null && payload.rankDelta < 0) {
    const down = Math.abs(payload.rankDelta);
    return (
      <>
        <span className="tq-down">
          ⬇ Down {down} spot{down > 1 ? "s" : ""}
        </span>{" "}
        — {here}
      </>
    );
  }
  // §12.4 — the domain owns the wording for "no change" and first placement.
  return `${describeRankDelta(payload.rankDelta ?? 0)} at ${ordinal(payload.overallRank)}`;
}
